import React, { useEffect, useState } from 'react'
import { useSprings, animated, interpolate } from 'react-spring'
import { useDrag } from 'react-use-gesture'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import './styles.scss'

// These two are just helpers, they curate spring data, values that are later being interpolated into css
const to = i => ({
  x: 0,
  y: 0, // i * -4,
  scale: 1,
  rot: 0, // -5 + Math.random() * 10,
  delay: i * 100
})
const from = i => ({ x: -5000, rot: 0, scale: 1.5, y: 0 })
// This is being used down there in the view, it interpolates rotation and scale into a css transform
const trans = (r, s) => `rotateY(${r / 10}deg) rotateZ(${r}deg) scale(${s})`

interface Props {
  data: []
}

export const Deck = ({ data }: Props) => {
  const [cards, setCards] = useState([])
  const [gone] = useState(() => new Set()) // The set flags all the cards that are flicked out
  const [props, set] = useSprings(cards.length, i => ({
    ...to(i),
    from: from(i)
  })) // Create a bunch of springs using the helpers above

  useEffect(() => {
    setCards(data.filter(d => d['Publié'] === 'TRUE'))
  }, [data])

  // Create a gesture, we're interested in down-state, delta (current-pos - click-pos), direction and velocity
  const bind = useDrag(
    ({ args: [index], down, movement: [mx], direction: [xDir], velocity }) => {
      const trigger = velocity > 0.2 // If you flick hard enough it should trigger the card to fly out
      const dir = xDir < 0 ? -1 : 1 // Direction should either point left or right
      if (!down && trigger) gone.add(index) // If button/finger's up and trigger velocity is reached, we flag the card ready to fly out
      set(i => {
        if (index !== i) return // We're only interested in changing spring-data for the current spring
        const isGone = gone.has(index)
        const x = isGone ? (200 + window.innerWidth) * dir : down ? mx : 0 // When a card is gone it flys out left or right, otherwise goes back to zero
        const rot = mx / 100 + (isGone ? dir * 10 * velocity : 0) // How much the card tilts, flicking it harder makes it rotate faster
        const scale = down ? 1.1 : 1 // Active cards lift up a bit
        return {
          x,
          rot,
          scale,
          delay: undefined,
          config: { friction: 50, tension: down ? 800 : isGone ? 200 : 500 }
        }
      })
      if (!down && gone.size === cards.length)
        setTimeout(() => gone.clear() || set(i => to(i)), 600)
    }
  )
  // Now we're just mapping the animated values to our view, that's it. Btw, this component only renders once. :-)
  return (
    <>
      <div
        className='swipe-helper'
        onClick={() => {
          const cardIndex = cards.length - 1 - gone.size
          gone.add(cardIndex)

          set(i => {
            if (i !== cardIndex) return
            const dir = Math.random() < 0.5 ? -1 : 1
            const x = (200 + window.innerWidth) * dir
            const scale = 1.1
            const rot = 10

            return {
              x,
              rot,
              scale,
              delay: undefined,
              config: { friction: 50, tension: 200 }
            }
          })

          if (gone.size === cards.length)
            setTimeout(() => gone.clear() || set(i => to(i)), 600)
        }}
      >
        <i className='ui icon chevron right' />
      </div>
      <div className='gizmo-shadow'>
        <div />
      </div>
      <div className='gizmo-shadow'>
        <div />
      </div>
      <div className='gizmo-shadow'>
        <div />
      </div>

      {props.map(({ x, y, rot, scale }, i) => {
        const card = cards[i]
        return (
          <animated.div
            key={i}
            style={{
              transform: interpolate(
                [x, y],
                (x, y) => `translate3d(${x}px,${y}px,0)`
              )
            }}
          >
            {/* This is the card itself, we're binding our gesture to it (and inject its index so we know which is which) */}
            <animated.div
              {...bind(i)}
              style={{
                // boxShadow: scale.value === 1.5 ? 'none' : '5px 5px 10px rgba(255,0,0,0.5)',
                transform: interpolate([rot, scale], trans)
                // backgroundImage: `url(${cards[i]})`
              }}
            >
              <div className='top'>
                {cards.length - i}
                <span className='small'>/{cards.length}</span>
              </div>
              <div className='middle'>
                <div
                  className='main'
                  dangerouslySetInnerHTML={{ __html: card.Texte }}
                />
                <div className='link'>
                  <a
                    target='_blank'
                    rel='noreferrer'
                    href={`https://www.${parseHostname().publicationName}.ch/${
                      card['ID Article']
                    }`}
                  >
                    {card['ID Article'] && <div>En lire davantage</div>}
                  </a>
                </div>
              </div>
              <div className='bottom'>
                <div
                  className='author'
                  dangerouslySetInnerHTML={{ __html: card.Auteur }}
                />
              </div>
            </animated.div>
          </animated.div>
        )
      })}
    </>
  )
}
